import { useCallback } from 'react';
import { Alert } from '@cmsgov/ds-medicare-gov';
import type { AppProps } from './types';
import type { FacetKey } from './constants';
import { FACETS } from './constants';
import { t } from './i18n';
import { useAppSearch } from './hooks/useAppSearch';
import type { SearchFilters } from './hooks/useAppSearch';
import { SearchBar } from './components/SearchBar';
import { FilterSet } from './components/FilterSet';
import { FilterChips } from './components/FilterChips';
import { FilterDrawer } from './components/FilterDrawer';
import { ResultsGrid } from './components/ResultsGrid';
import { LoadMore } from './components/LoadMore';
import './App.css';

/**
 * App Library Search main component.
 */
export default function App({
  lang,
  pagingLimit,
  displaySearch,
  noResultsMessage,
}: AppProps) {
  const {
    apps,
    facets,
    meta,
    loading,
    error,
    filters,
    search,
    loadMore,
    resetFilters,
  } = useAppSearch(pagingLimit);

  const strings = t(lang);
  const noResultsHtml = lang !== 'en'
    ? noResultsMessage?.es
    : noResultsMessage?.en;

  // Treat only the initial load failure as a fatal state.
  // Later search failures should not replace an already rendered UI.
  const fetchFailed = !loading && meta === null && error !== null;

  // Build active filters record from current filters
  const activeFilters: Record<FacetKey, number[]> = {
    [FACETS.PRICE_TYPE]: filters.priceType || [],
    [FACETS.PLATFORMS]: filters.platforms || [],
    [FACETS.CONDITION]: filters.condition || [],
    [FACETS.FEATURES]: filters.features || [],
  };

  // Current keyword from the search bar (kept in filters state)
  const currentKeyword = filters.keyword || '';

  const handleSearch = useCallback(
    (keyword: string) => {
      search({ ...filters, keyword });
    },
    [filters, search]
  );

  const handleFilterApply = useCallback(
    (allFilters: Record<FacetKey, number[]>) => {
      search({
        keyword: currentKeyword,
        priceType: allFilters[FACETS.PRICE_TYPE],
        platforms: allFilters[FACETS.PLATFORMS],
        condition: allFilters[FACETS.CONDITION],
        features: allFilters[FACETS.FEATURES],
      });
    },
    [currentKeyword, search]
  );

  const handleChipRemove = useCallback(
    (facetKey: FacetKey, tid: number) => {
      const updated: SearchFilters = { ...filters };
      switch (facetKey) {
        case FACETS.PRICE_TYPE:
          updated.priceType = (filters.priceType || []).filter((id) => id !== tid);
          break;
        case FACETS.PLATFORMS:
          updated.platforms = (filters.platforms || []).filter((id) => id !== tid);
          break;
        case FACETS.CONDITION:
          updated.condition = (filters.condition || []).filter((id) => id !== tid);
          break;
        case FACETS.FEATURES:
          updated.features = (filters.features || []).filter((id) => id !== tid);
          break;
      }
      search(updated);
    },
    [filters, search]
  );

  const handleDrawerApply = useCallback(
    (drawerFilters: Record<FacetKey, number[]>) => {
      search({
        keyword: currentKeyword,
        priceType: drawerFilters[FACETS.PRICE_TYPE],
        platforms: drawerFilters[FACETS.PLATFORMS],
        condition: drawerFilters[FACETS.CONDITION],
        features: drawerFilters[FACETS.FEATURES],
      });
    },
    [currentKeyword, search]
  );

  const handleReset = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  // Disabled only when the initial fetch failed completely
  const isDisabled = fetchFailed;

  if (fetchFailed) {
    return (
      <div className="app-library-search" data-lang={lang}>
        <Alert variation="error" className="ds-u-margin-top--3">
          {strings.noAppsAvailable}
        </Alert>
      </div>
    );
  }

  return (
    <div className="app-library-search" data-lang={lang}>
      {displaySearch && (
        <div className="app-library-search__controls">
          <SearchBar
            key={filters.keyword || ''}
            lang={lang}
            onSearch={handleSearch}
            initialKeyword={filters.keyword}
          />
          <FilterSet
            lang={lang}
            facets={facets}
            activeFilters={activeFilters}
            disabled={isDisabled}
            totalResults={meta?.total || 0}
            onApply={handleFilterApply}
          />
          <FilterDrawer
            lang={lang}
            facets={facets}
            activeFilters={activeFilters}
            totalResults={meta?.total || 0}
            onApply={handleDrawerApply}
            onClear={handleReset}
          />
          <FilterChips
            lang={lang}
            facets={facets}
            activeFilters={activeFilters}
            onRemove={handleChipRemove}
            onReset={handleReset}
          />
        </div>
      )}

      {meta !== null && (
        <ResultsGrid
          lang={lang}
          apps={apps}
          loading={loading}
          total={meta?.total || 0}
          noResultsHtml={noResultsHtml}
        />
      )}

      <LoadMore
        lang={lang}
        hasMore={meta?.has_more || false}
        loading={loading}
        onLoadMore={loadMore}
      />
    </div>
  );
}
